import { useState, type ReactNode } from "react";

import type { DashboardMetric } from "@/shared/api/api-client";
import { ChartControls, ChartToggle } from "@/shared/components/charts/chart-kit";
import { EmptyState } from "@/shared/components/charts/EmptyState";
import { Panel } from "@/shared/components/charts/Panel";
import { PolarityBars } from "@/shared/components/charts/PolarityBars";
import { Select } from "@/shared/components/Select";
import { formatInteger } from "@/shared/utils/format";

import {
  INDICATORS,
  indicatorLabel,
  indicatorText,
  LIMITS,
  limited,
  sliceLabel,
  toBarItems,
  topHighlights,
  type Indicator,
  type LabelKind,
  type Limit
} from "./dashboard-v1";

export type PanelMode = "absolute" | "percent";

export const MODE_OPTIONS: Array<{ value: PanelMode; label: string }> = [
  { value: "absolute", label: "123" },
  { value: "percent", label: "%" }
];

/**
 * Um "Sentimento & Indicadores por …" do v1: o volume de cada categoria
 * empilhado por sentimento, com o indicador escolhido ao lado, a
 * quantidade de categorias e o 123/%. `highlights` liga os cinco cartões
 * de destaque que o v1 punha embaixo do gráfico. `extra` são controles a
 * mais (a dimensão, a rede) que a tela de cima decide.
 */
export function PolarityPanel({
  title,
  subtitle,
  items,
  kind = "default",
  className = "dashboard-span-12",
  showsCena,
  withIndicator = true,
  defaultLimit = "10",
  highlights = false,
  extra,
  unavailable
}: {
  title: string;
  subtitle: string;
  items: DashboardMetric[];
  kind?: LabelKind;
  className?: string;
  showsCena: boolean;
  withIndicator?: boolean;
  defaultLimit?: Limit;
  highlights?: boolean;
  extra?: ReactNode;
  /** Por que a foto não tem o dado — a tela diz isso em vez de mostrar
   *  um gráfico vazio que culparia o filtro. */
  unavailable?: string;
}) {
  const indicators = INDICATORS.filter(option => showsCena || option.value !== "cena");
  const [indicator, setIndicator] = useState<Indicator>(indicators[0]?.value ?? "interactions");
  const [limit, setLimit] = useState<Limit>(defaultLimit);
  const [mode, setMode] = useState<PanelMode>("absolute");
  const shown = limited(items, limit);
  const best = highlights ? topHighlights(items, withIndicator ? indicator : "count") : [];

  return (
    <Panel
      title={title}
      subtitle={subtitle}
      className={className}
      downloadable={!unavailable}
    >
      {unavailable ? <EmptyState label={unavailable}/> : (
        <>
          <ChartControls>
            {extra}
            {withIndicator && (
              <Select name={`${title}-indicador`} aria-label="Indicador" value={indicator} onChange={event => setIndicator(event.target.value as Indicator)}>
                {indicators.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </Select>
            )}
            <Select name={`${title}-quantidade`} aria-label="Quantidade" value={limit} onChange={event => setLimit(event.target.value as Limit)}>
              {LIMITS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Select>
            <ChartToggle label="Escala" value={mode} options={MODE_OPTIONS} onChange={setMode}/>
          </ChartControls>
          <div className="dashboard-chart-gap">
            <PolarityBars
              items={toBarItems(shown, { indicator: withIndicator ? indicator : undefined, kind })}
              percent={mode === "percent"}
              metricLabel={withIndicator ? indicatorLabel(indicator) : undefined}
            />
          </div>
          {items.length > shown.length && (
            <p className="dashboard-chart-note">Mostrando {shown.length} de {formatInteger(items.length)}.</p>
          )}
          {best.length > 0 && (
            <ol className="dashboard-highlights" aria-label="Destaques">
              {best.map((item, index) => (
                <li key={`${item.parent ?? ""}|${item.key}`}>
                  <span className="dashboard-highlight-rank">{index + 1}º</span>
                  <strong title={sliceLabel(item, kind)}>{sliceLabel(item, kind)}</strong>
                  <span className="dashboard-highlight-value">
                    {withIndicator ? `${indicatorLabel(indicator)}: ${indicatorText(item, indicator)}` : `${formatInteger(item.count)} menções`}
                  </span>
                  <small>{formatInteger(item.count)} menções</small>
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </Panel>
  );
}
